'use client';
import React, { useState, useEffect, useRef } from 'react';
import { X, Volume2, VolumeX, Maximize2, SkipForward } from 'lucide-react';

interface PreRollAdOverlayProps {
  open: boolean;
  onClose: () => void;
  movieTitle: string;
}

export default function PreRollAdOverlay({ open, onClose, movieTitle }: PreRollAdOverlayProps) {
  const [countdown, setCountdown] = useState(5);
  const [adPhase, setAdPhase] = useState<'ad' | 'movie'>('ad');
  const [muted, setMuted] = useState(false);
  const [adProgress, setAdProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!open) {
      setCountdown(5);
      setAdPhase('ad');
      setAdProgress(0);
      return;
    }

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          setAdPhase('movie');
          return 0;
        }
        return prev - 1;
      });
      setAdProgress((prev) => Math.min(prev + 20, 100));
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 ad-overlay flex items-center justify-center">
      {adPhase === 'ad' ? (
        <div className="relative w-full max-w-4xl mx-4 rounded-2xl overflow-hidden bg-black border border-border fade-in">
          {/* Ad video placeholder — backend: replace with actual AdSense/DFP pre-roll slot */}
          <div className="relative aspect-video bg-gradient-to-br from-secondary to-muted flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-3 border border-accent/30">
                <span className="text-accent text-2xl font-800">AD</span>
              </div>
              <p className="text-secondary-foreground text-sm font-500">
                Reklamë Google AdSense
              </p>
              <p className="text-muted-foreground text-xs mt-1">
                Filmi fillon pas {countdown}s
              </p>
            </div>

            {/* Ad progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
              <div
                className="h-full bg-primary transition-all duration-1000"
                style={{ width: `${adProgress}%` }}
              />
            </div>

            {/* Controls */}
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
              <span className="px-2 py-1 bg-black/60 text-accent text-xs font-700 rounded-md border border-accent/30">
                REKLAMË
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMuted(!muted)}
                  className="w-8 h-8 flex items-center justify-center bg-black/60 text-foreground rounded-lg hover:bg-black/80 transition-all"
                  aria-label={muted ? 'Aktivizo zërin' : 'Hiq zërin'}
                >
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
              </div>
            </div>

            {/* Skip countdown */}
            <div className="absolute bottom-6 right-4">
              {countdown > 0 ? (
                <div className="flex items-center gap-1.5 px-3 py-2 bg-black/70 text-secondary-foreground text-xs font-600 rounded-lg border border-border">
                  <SkipForward size={12} />
                  Kalo pas {countdown}s
                </div>
              ) : (
                <button
                  onClick={() => setAdPhase('movie')}
                  className="flex items-center gap-1.5 px-3 py-2 bg-foreground text-background text-xs font-700 rounded-lg hover:bg-foreground/90 active:scale-95 transition-all"
                >
                  <SkipForward size={12} />
                  Kalo Reklamën
                </button>
              )}
            </div>
          </div>

          <div className="p-4 bg-card flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-500">Duke u përgatitur</p>
              <p className="text-sm font-700 text-foreground">{movieTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-2 text-muted-foreground hover:text-foreground text-xs font-500 rounded-lg hover:bg-muted transition-all"
            >
              <X size={14} />
              Mbyll
            </button>
          </div>
        </div>
      ) : (
        /* Movie player phase */
        <div className="relative w-full max-w-4xl mx-4 rounded-2xl overflow-hidden bg-black border border-border fade-in">
          <div className="relative aspect-video bg-gradient-to-br from-black to-secondary flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary/40 cursor-pointer hover:bg-primary/30 transition-all">
                <svg viewBox="0 0 24 24" className="w-10 h-10 fill-primary" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <p className="text-foreground font-700 text-lg">{movieTitle}</p>
              <p className="text-secondary-foreground text-sm mt-1">
                Duke luajtur — {/* backend: embed actual stream URL here */}
              </p>
            </div>
            <div className="absolute top-3 right-3 flex items-center gap-2">
              <button
                className="w-8 h-8 flex items-center justify-center bg-black/60 text-foreground rounded-lg hover:bg-black/80 transition-all"
                aria-label="Ekran i plotë"
              >
                <Maximize2 size={14} />
              </button>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center bg-black/60 text-foreground rounded-lg hover:bg-black/80 transition-all"
                aria-label="Mbyll player"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}